import "./index.css";
import { Checkbox, Tooltip } from "antd";
import React, { useCallback, useMemo, useState } from "react";

import ICON_ARROW from "../../../assets/arrow.svg";

import type { TypeGuideline } from "../../../hooks/useAllPrograms";

const LENDER_LOGO_CONFIG: Record<string, string> = {
  "5th Street Capital": "5thStreetCapital.png",
  "Angel Oak": "AngelOak.png",
  "AmeriHome Mortgage": "AmeriHome.png",
  "AAA LENDINGS": "AAALending.png",
  "AD Mortgage": "ADMortgage.png",
  "AmWest": "AmWest.png",
  "Acra Lending": "AcraLending.png",
  "Arc Home": "ArcHome.png",
  "American Heritage Lending": "AmericanHeritageLending.png",
  "American Financial Resources": "AmericanFinancialResources.jpg",
  "Axos Bank": "AxosBank.png",
  "Advancial CU": "AdvancialCU.png",
  "Brokers First Funding": "BrokersFirstFunding.png",
  "Bluepoint": "Bluepoint.png",
  "Bluebono LLC": "Bluebono.png",
  "Cake": "Cake.jpg",
  "Cardinal Financial": "CardinalFinancial.png",
  "Change Wholesale": "ChangeWholesale.png",
  "CMG Financial": "CMGFinancial.png",
  "Chase Correspondent Lending": "Chase.png",
  "Citi Correspondent Lending": "Citi.png",
  "ClearEdge Lending": "ClearEdgeLending.png",
  "Dart Bank": "DartBank.png",
  "EPM": "EPM.png",
  "eLEND": "eLEND.jpg",
  "EMET Mortgage": "EMETMortgage.png",
  "First Colony Mortgage": "FirstColonyMortgage.png",
  "Forward Lending": "ForwardLending.png",
  "Freedom Mortgage": "FreedomMortgage.jpg",
  "Fifth Third Bank": "FifthThirdBank.png",
  "FundLoans": "FundLoans.png",
  "FNBA": "FNBA.png",
  "Flipside Lending": "FlipsideLending.jpg",
  "Giant Lending": "GiantLending.jpg",
  "Googain-Bahamas": "Googain.png",
  "Googain-Glory": "Googain.png",
  "Googain-Panther": "Googain.png",
  "Googain-Pebbles": "Googain.png",
  "Googain-Willow": "Googain.png",
  "Gold Star": "GoldStar.jpg",
  "General Mortgage Capital Corporation": "GeneralMortgageCapitalCorporation.png",
  "HomeXpress Mortgage Corp": "HomeXpressMortgageCorp.jpg",
  "Huntington Bank": "HuntingtonBank.png",
  "Homebridge Wholesale": "HomebridgeWholesale.png",
  "JMAC Lending": "JMACLending.png",
  "Kind Lending": "KindLending.jpg",
  "Kiavi": "Kiavi.png",
  "Lending Pros": "LendingPros.png",
  "LoanStream": "LoanStream.png",
  "Logan": "Logan.png",
  "Luxury Mortgage": "Luxury.jpg",
  "Lakeview": "Lakeview.jpg",
  "LendSure": "LendSure.png",
  "Mega Capital": "MegaCapital.png",
  "MK Lending": "MKLending.png",
  "Mr. Cooper": "MrCooper.jpg",
  "Mutual of Omaha": "MutualofOmaha.png",
  "Mortgage Land Capital": "MortgageLandCapital.png",
  "Newfi Lending": "NewfiLending.png",
  "New Wave Lending": "NewWaveLending.png",
  "NMSI": "NMSI.jpg",
  "Nexbank": "Nexbank.png",
  "Orion Lending": "OrionLending.jpg",
  "Oaktree": "Oaktree.png",
  "PRMG": "PRMG.png",
  "PennyMac": "PennyMac.png",
  "Plaza": "Plaza.png",
  "PHH Mortgage": "PHH.png",
  "Provident Funding": "ProvidentFunding.png",
  "Quontic Wholesale": "QuonticWholesale.png",
  "Royal Business Bank": "RoyalBusinessBank.png",
  "The Lender": "TheLender.png",
  "The Loan Store": "TheLoanStore.png",
  "Thunderbird": "Thunderbird.png",
  "TMS": "TMS.jpg",
  "Triad": "Triad.png",
  "Titan Financial Solutions LLC": "TitanFinancialSolutions.jpg",
  "MAXEX": "MAXEX.png",
  "Newrez": "Newrez.png",
  "Bayview": "Bayview.png",
  "Greenbox": "Greenbox.png",
  "NQM Funding": "NQM.png",
  "Deephaven": "Deephaven.png",
  "Resicentral": "Resicentral.jpg",
  "SG Capital Partners": "SG.png",
  "Silver Hill Capital": "SilverHillCapital.jpg",
  "Spring EQ": "SpringEQ.png",
  "Sun West Mortgage": "SunWestMortgage.png",
  "Symmetry Lending": "SymmetryLending.jpg",
  "Champions Funding": "Champions.png",
  "Union Home Mortgage": "UnionHomeMortgage.png",
  "United AG Lending": "UnitedAGLending.png",
  "UWM": "UWM.jpg",
  "Verus Mortgage Capital": "Verus.png",
  "Village Capital & Investment": "VillageCapital.jpg",
  "Windsor Mortgage": "WindsorMortgage.jpg",
  "New Wave Lending Group": "NewWaveLending.png",
  "CalHFA": "CalHFA.png",
  "Nations Direct": "NationDirect.webp",
};

export const loadLenderLogo = (lenderName: string) => {
  const fileName = LENDER_LOGO_CONFIG[lenderName];
  if (!fileName) return null;
  
  try {
    return `/lender_icon/${fileName}`; // 为了避免打包成base64，图标都放在 public/lender_logo 目录
  } catch (error) {
    console.warn(`Logo not found for lender: ${lenderName}`);
    return null;
  }
};

export const ICON = new Proxy({} as Record<string, any>, {
  get: (target, prop: string) => {
    if (!target[prop]) {
      target[prop] = loadLenderLogo(prop);
    }
    return target[prop];
  }
});

export interface TypeGuidelineCheckboxProps {
  name: string;
  value?: string[];
  options: TypeGuideline[];
  /**
   * @param count 数量
   * @description 传递number则显示
   */
  count?: number;
  onChange?(e: string): void;
  /**
   * @name onExpand 展开/收起
   */
  onExpand?(): void;
  /**
   * @name onPreview 预览 “Guideline”
   */
  onPreview?(data: TypeGuideline): void;
  /**
   * @param expand 展开状态
   */
  expand?: boolean;
  /**
   * @param noEditing 禁止选中、仅做查阅
   * @description 会通过css将 checkbox 全部屏蔽掉
   */
  noEditing?: boolean;
  /**
   * @param showExpand 显示展开图标
   */
  showExpand?: boolean;
  /**
   * @param disabled 是否禁用lender、guideline的checkbox
   * @description 对应的是"数据库"状态，目前细粒度没那么高，如果错了就全部禁用
   */
  disabled?: boolean;
}

/**
 * @name GuidelineCheckbox 指南选择
 */
const GuidelineCheckbox: React.FC<TypeGuidelineCheckboxProps> = ({
  name,
  value,
  count,
  expand,
  options,
  children,
  disabled,
  onChange,
  onExpand,
  onPreview,
  noEditing,
  showExpand
}) => {
  const [expandState, setExpandState] = useState(expand);

  const { MAP, UNCHECKED, CHECKED_LENGTH } = useMemo(() => {
    const valueSet = new Set(value);
    const MAP: Record<string, boolean> = {};
    const UNCHECKED: string[] = [];
    const LENGTH = options?.length;
    for (let i = 0; i < LENGTH; i++) {
      const v = options[i];
      const checked = valueSet.has(v.id);
      MAP[v.id] = checked;
      checked || UNCHECKED.push(v.id);
    }
    return { MAP, UNCHECKED, CHECKED_LENGTH: options.length - UNCHECKED.length };
  }, [options, value]);

  const IS_ALL = options.length === CHECKED_LENGTH;

  const onSelectAll = useCallback(() => {
    if (noEditing) return;
    if (IS_ALL) {
      for (const v of options) onChange?.(v.id);
    } else {
      for (const id of UNCHECKED) onChange?.(id);
    }
  }, [noEditing, IS_ALL, options, UNCHECKED, onChange]);

  const onExpandChange = useCallback(() => {
    onExpand ? onExpand() : setExpandState(b => !b)
  }, [onExpand]);

  const onStopEvent = useCallback((e: React.MouseEvent<HTMLInputElement>) => {
    e.stopPropagation();
  }, []);

  function onPreviewPDF(e: React.MouseEvent<HTMLSpanElement>, value: TypeGuideline) {
    e.preventDefault();
    onPreview?.(value);
  }

  const OPEN = onExpand ? expand : expandState;
  
  const STYLE = useMemo(() => ({
    transition: "transform 0.3s ease",
    transform: `rotate(${OPEN ? 0 : 180}deg)`,
  }), [OPEN]);

  const ICON_LENDER = ICON?.[name];

  return (
    <div
      className={`guidelines-select ${
        noEditing ? "guidelines-select-no-edit" : ""
      }`}
      style={{ height: OPEN ? "max-content" : 42 }}
    >
      <header onClick={onExpandChange} className="guidelines-select-header">
        <span onClick={onStopEvent}>
          <Tooltip title={disabled ? 'This lender is temporarily unavailable.' : undefined}>
            <Checkbox
              disabled={disabled}
              onChange={onSelectAll}
              checked={disabled ? undefined : IS_ALL}
              className="guidelines-select-header-checkbox"
              indeterminate={disabled ? undefined : (IS_ALL ? false : Boolean(CHECKED_LENGTH))}
            >
              {ICON_LENDER && (
                <img src={ICON[name]} className="guidelines-select-header-icon" alt={name} />
              )}
              {name}
              {typeof count === "number" && (
                <span className="guidelines-group-header-count">{count}</span>
              )}
            </Checkbox>
          </Tooltip>
        </span>
        <div>
          {children}
          {showExpand && <img alt="#" style={STYLE} src={ICON_ARROW} />}
        </div>
      </header>
      <ul style={{ display: OPEN ? 'block' : 'none' }} className="ant-checkbox-group">
        {options.map((v) => (
         <li key={v.id} onClick={() => noEditing || onChange?.(v.id)}>
           <Tooltip title={disabled ? 'This guideline is temporarily unavailable.' : undefined}>
              <Checkbox
                disabled={disabled}
                checked={MAP[v.id]}
                onClick={onStopEvent}
                onChange={() => noEditing || onChange?.(v.id)}
              >
                <span 
                  title="Click on the name to preview" 
                  onClick={disabled ? undefined : e => onPreviewPDF(e, v)} 
                  className={disabled ? undefined : "guidelines-group-text-click"}
                >
                    {v.displayName}
                  </span>
              </Checkbox>
            </Tooltip>
         </li>
        ))}
      </ul>
    </div>
  );
};

export default GuidelineCheckbox;
